import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { el } from 'date-fns/locale';
import { ArrowLeft, ArrowRight, Eye, EyeOff, FilterX, MessageSquare, Search } from 'lucide-react';
import { fetchAdminLeads } from '@/api/adminLeads';
import type { LeadStageType, ServiceType } from '@/context/LeadContext';
import { AdminErrorState } from '@/components/admin/AdminDataState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { FriendlyIllustration } from '@/components/ui/FriendlyIllustration';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { SERVICE_OPTIONS as CATALOG_SERVICE_OPTIONS } from '@/lib/serviceCatalog';

interface PartnerLeadsListProps {
  partnerId: string;
  stageTotals?: Partial<Record<'all' | LeadStageType, number>>;
}

const SERVICE_OPTIONS: Array<{ value: ServiceType; label: string }> = CATALOG_SERVICE_OPTIONS.map(({ value, label }) => ({ value, label }));

const STAGE_OPTIONS: Array<{ value: 'all' | LeadStageType; label: string }> = [
  { value: 'all', label: 'Όλα' },
  { value: 'Νέο', label: 'Νέα' },
  { value: 'σεΕκκρεμότητα', label: 'Σε εκκρεμότητα' },
  { value: 'ήθελεΠληροφορίες', label: 'Πληροφορίες' },
  { value: 'έκλεισεΗΔουλειά', label: 'Έκλεισαν' },
];

const STAGE_BADGE: Record<LeadStageType, string> = {
  Νέο: 'border-violet-200 bg-violet-50 text-violet-700',
  σεΕκκρεμότητα: 'border-amber-200 bg-amber-50 text-amber-700',
  ήθελεΠληροφορίες: 'border-sky-200 bg-sky-50 text-sky-700',
  έκλεισεΗΔουλειά: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};

const stageLabel = (stage: LeadStageType) => STAGE_OPTIONS.find((item) => item.value === stage)?.label || stage;
const serviceLabel = (service: ServiceType) => SERVICE_OPTIONS.find((item) => item.value === service)?.label || service;

const createdAfterForPeriod = (period: string): string | undefined => {
  if (period === 'all') return undefined;
  const date = new Date();
  date.setDate(date.getDate() - Number(period));
  return date.toISOString();
};

const PartnerLeadsList = ({ partnerId, stageTotals = {} }: PartnerLeadsListProps) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stage, setStage] = useState<'all' | LeadStageType>('all');
  const [service, setService] = useState<'all' | ServiceType>('all');
  const [period, setPeriod] = useState('30');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => setPage(1), [debouncedSearch, stage, service, period, partnerId]);

  const query = useQuery({
    queryKey: ['admin-partner-leads', partnerId, page, debouncedSearch, stage, service, period],
    queryFn: () => fetchAdminLeads({
      partnerId,
      page,
      perPage: 25,
      search: debouncedSearch,
      stage,
      service,
      createdAfter: createdAfterForPeriod(period),
      sort: 'newest',
    }),
    enabled: partnerId !== '',
    staleTime: 30_000,
  });

  const leads = query.data?.leads || [];
  const pagination = query.data?.pagination || { page: 1, per_page: 25, total: 0, total_pages: 1 };
  const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.per_page + 1;
  const to = Math.min(pagination.page * pagination.per_page, pagination.total);
  const activeFilters = Number(Boolean(search.trim())) + Number(stage !== 'all') + Number(service !== 'all') + Number(period !== '30');

  const resetFilters = () => {
    setSearch('');
    setStage('all');
    setService('all');
    setPeriod('30');
  };

  const serviceOptions = useMemo(() => SERVICE_OPTIONS, []);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto pb-1">
        <div className="flex min-w-max gap-2">
          {STAGE_OPTIONS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setStage(item.value)}
              className={cn(
                'flex h-11 items-center gap-2 rounded-xl border px-3.5 text-sm font-semibold transition-colors',
                stage === item.value ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
              )}
            >
              {item.label}
              {stageTotals[item.value] !== undefined && <span className={cn('rounded-full px-1.5 py-0.5 text-[10px]', stage === item.value ? 'bg-white/15 text-white' : 'bg-slate-100 text-slate-500')}>{stageTotals[item.value]}</span>}
            </button>
          ))}
        </div>
      </div>

      <Card className="rounded-2xl border-slate-200 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(240px,1fr)_180px_210px_180px_auto]">
            <div className="relative sm:col-span-2 xl:col-span-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Όνομα, τηλέφωνο, περιοχή ή ΤΚ" className="h-11 rounded-xl pl-10" aria-label="Αναζήτηση leads συνεργάτη" />
            </div>
            <Select value={period} onValueChange={setPeriod}><SelectTrigger className="h-11 rounded-xl" aria-label="Περίοδος leads"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="7">7 ημέρες</SelectItem><SelectItem value="30">30 ημέρες</SelectItem><SelectItem value="90">90 ημέρες</SelectItem><SelectItem value="365">1 έτος</SelectItem><SelectItem value="all">Όλο το ιστορικό</SelectItem></SelectContent></Select>
            <Select value={service} onValueChange={(value) => setService(value as 'all' | ServiceType)}><SelectTrigger className="h-11 rounded-xl" aria-label="Υπηρεσία leads"><SelectValue placeholder="Υπηρεσία" /></SelectTrigger><SelectContent><SelectItem value="all">Όλες οι υπηρεσίες</SelectItem>{serviceOptions.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent></Select>
            <Select value={stage} onValueChange={(value) => setStage(value as 'all' | LeadStageType)}><SelectTrigger className="h-11 rounded-xl" aria-label="Στάδιο leads"><SelectValue placeholder="Στάδιο" /></SelectTrigger><SelectContent>{STAGE_OPTIONS.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent></Select>
            <Button type="button" variant="outline" onClick={resetFilters} disabled={activeFilters === 0} className="h-11 rounded-xl"><FilterX className="mr-2 h-4 w-4" /> Καθαρισμός</Button>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4 text-xs text-slate-500">
            <span>{query.isFetching ? 'Ανανέωση…' : pagination.total ? `${from}–${to} από ${pagination.total} leads` : 'Κανένα αποτέλεσμα'}</span>
            <span>25 ανά σελίδα · server-side φίλτρα</span>
          </div>
        </CardContent>
      </Card>

      {query.isLoading ? (
        <div className="space-y-3">{Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-24 rounded-2xl" />)}</div>
      ) : query.isError ? (
        <AdminErrorState message={query.error instanceof Error ? query.error.message : 'Δεν φορτώθηκαν τα leads.'} onRetry={() => void query.refetch()} />
      ) : leads.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-14 text-center"><FriendlyIllustration kind="lead-flow" className="mx-auto h-32 w-auto max-w-[220px] drop-shadow-[0_12px_20px_rgba(14,116,190,0.12)]" /><p className="mt-2 font-semibold text-slate-800">Δεν βρέθηκαν leads</p><p className="mt-1 text-sm text-slate-500">Δοκιμάστε άλλη περίοδο ή καθαρίστε τα φίλτρα.</p></div>
      ) : (
        <>
          <div className="space-y-3 sm:hidden">
            {leads.map((lead) => (
              <Link key={lead.id} to={`/admin/lead/${encodeURIComponent(lead.id)}`} className="block rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-colors active:bg-slate-50">
                <div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="truncate font-bold text-slate-950">{lead.name}</p><p className="mt-1 text-sm font-medium text-slate-600">{lead.phone}</p></div><Badge className={cn('shrink-0 border', STAGE_BADGE[lead.stage])}>{stageLabel(lead.stage)}</Badge></div>
                <div className="mt-3 grid gap-2 text-xs text-slate-500"><p className="truncate">{serviceLabel(lead.service)} · {lead.address || lead.postalCode || 'Χωρίς περιοχή'}</p><p>{format(new Date(lead.createdAt), 'dd/MM/yyyy · HH:mm', { locale: el })} · {lead.timeframe === 'άμεσα' ? 'Άμεσα' : lead.timeframe === 'εντός3μηνών' ? 'Εντός 3 μηνών' : 'Έρευνα αγοράς'}</p></div>
                <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3 text-xs"><span className={cn('flex items-center gap-1.5', lead.viewed ? 'text-emerald-700' : 'text-slate-400')}>{lead.viewed ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}{lead.viewed ? 'Προβλήθηκε' : 'Δεν προβλήθηκε'}</span><span className={cn('ml-auto flex items-center gap-1.5', lead.smsStatus === 'sent' ? 'text-sky-700' : 'text-slate-400')}><MessageSquare className="h-3.5 w-3.5" />{lead.smsStatus === 'sent' ? 'SMS sent' : lead.smsStatus === 'failed' ? 'SMS failed' : 'Χωρίς SMS status'}</span></div>
              </Link>
            ))}
          </div>

          <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white sm:block">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Επαφή</TableHead><TableHead>Υπηρεσία</TableHead><TableHead>Στάδιο</TableHead><TableHead>Προβολή / SMS</TableHead><TableHead>Δημιουργία</TableHead><TableHead className="text-right">Ενέργεια</TableHead></TableRow></TableHeader>
                <TableBody>{leads.map((lead) => <TableRow key={lead.id}><TableCell><p className="font-semibold text-slate-900">{lead.name}</p><p className="mt-1 text-xs text-slate-500">{lead.phone} · {lead.address || lead.postalCode || 'Χωρίς περιοχή'}</p></TableCell><TableCell><Badge variant="outline">{serviceLabel(lead.service)}</Badge><p className="mt-1 text-xs text-slate-500">{lead.timeframe === 'άμεσα' ? 'Άμεσα' : lead.timeframe === 'εντός3μηνών' ? 'Εντός 3 μηνών' : 'Έρευνα αγοράς'}</p></TableCell><TableCell><Badge className={cn('border', STAGE_BADGE[lead.stage])}>{stageLabel(lead.stage)}</Badge></TableCell><TableCell><p className={cn('flex items-center gap-1.5 text-xs', lead.viewed ? 'text-emerald-700' : 'text-slate-400')}>{lead.viewed ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}{lead.viewed ? 'Προβλήθηκε' : 'Δεν προβλήθηκε'}</p><p className={cn('mt-1 flex items-center gap-1.5 text-xs', lead.smsStatus === 'sent' ? 'text-sky-700' : lead.smsStatus === 'failed' ? 'text-rose-700' : 'text-slate-400')}><MessageSquare className="h-3.5 w-3.5" />{lead.smsStatus === 'sent' ? 'SMS sent' : lead.smsStatus === 'failed' ? 'SMS failed' : 'Χωρίς SMS'}</p></TableCell><TableCell><p className="text-sm text-slate-700">{format(new Date(lead.createdAt), 'dd/MM/yyyy · HH:mm', { locale: el })}</p><p className="mt-1 text-xs text-slate-400">{formatDistanceToNow(new Date(lead.createdAt), { addSuffix: true, locale: el })}</p></TableCell><TableCell className="text-right"><Button asChild variant="outline" className="h-11 rounded-xl"><Link to={`/admin/lead/${encodeURIComponent(lead.id)}`}>Άνοιγμα <ArrowRight className="ml-2 h-4 w-4" /></Link></Button></TableCell></TableRow>)}</TableBody>
              </Table>
            </div>
          </div>
        </>
      )}

      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3">
          <Button type="button" variant="outline" className="h-11 rounded-xl" disabled={page <= 1 || query.isFetching} onClick={() => setPage((value) => Math.max(1, value - 1))}><ArrowLeft className="mr-2 h-4 w-4" /> Προηγούμενη</Button>
          <p className="text-sm font-semibold text-slate-700">{pagination.page} / {pagination.total_pages}</p>
          <Button type="button" variant="outline" className="h-11 rounded-xl" disabled={page >= pagination.total_pages || query.isFetching} onClick={() => setPage((value) => Math.min(pagination.total_pages, value + 1))}>Επόμενη <ArrowRight className="ml-2 h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
};

export default PartnerLeadsList;
